import { useState, useCallback, useEffect, useRef } from "react";
import { Button, Modal, Spin } from "antd";
import type {
  CatalogSyncResult,
  CatalogSyncStartResponse,
  ProcessDefinition,
  ProcessLogEntry,
  ProcessLogResponse,
} from "../../types/catalog";
import {
  buildCatalogSyncResult,
  CATALOG_PROCESS_INTEGRATION,
  CATALOG_PROCESS_NOT_CONFIGURED_MESSAGE,
  CATALOG_POLL_INTERVAL_MS,
  CATALOG_POLL_MAX_WAIT_MS,
  CATALOG_SYNC_ERROR_MESSAGE,
  CATALOG_SYNC_TIMEOUT_MESSAGE,
  CATALOG_SYNC_URL,
  findCatalogSyncLogAfter,
  getLatestCatalogLogId,
  isCatalogSyncRunningStatus,
  isCatalogSyncTerminalStatus,
  LOG_LOOK_URL,
  LOG_PROCESS_URL,
  parseProcessLogList,
  translateCatalogMessage,
} from "../../types/catalog";
import {
  clearCatalogSyncSession,
  getCatalogSyncSession,
  hasActiveCatalogSyncSession,
  setCatalogSyncSession,
} from "../../utils/catalogSyncSession";

type ModalPhase = "confirm" | "loading" | "result";

interface UseUpdateCatalogOptions {
  onSyncComplete?: () => void;
}

interface UseUpdateCatalogReturn {
  syncLoading: boolean;
  syncResult: CatalogSyncResult | null;
  clearSyncResult: () => void;
  openSyncModal: () => void;
  renderSyncModal: () => JSX.Element;
}

const MODAL_TITLE = "Sincronización de Catálogo Trendy";

const MODAL_BODY =
  "Para garantizar que los datos de tu tienda se actualicen de manera correcta y sin errores, recuerda que el flujo de sincronización de la plataforma debe seguir este orden estricto:";

const MODAL_STEPS = ["Catálogo", "Inventario", "Precios"];

const SYNC_RUNNING_TITLE = "Sincronizando catálogo...";

const SYNC_RUNNING_LINES = [
  "Estamos actualizando la información de tus productos.",
  "Este proceso puede tardar unos minutos.",
  "Por favor, no cierres esta ventana hasta que la sincronización se haya completado.",
];

class SyncPollAbortedError extends Error {
  constructor() {
    super("Sync polling aborted");
    this.name = "SyncPollAbortedError";
  }
}

const getBearerToken = (): string => {
  const tokenStorage = localStorage.getItem("token");
  const token = tokenStorage ? tokenStorage.replace(/['"]+/g, "") : null;

  if (!token) {
    throw new Error("Token de acceso no encontrado. Inicia sesión primero.");
  }

  return token;
};

const authHeaders = (token: string): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

const resolveCatalogProcessTypeId = async (token: string): Promise<number> => {
  const response = await fetch(LOG_PROCESS_URL, {
    method: "GET",
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw new Error(CATALOG_SYNC_ERROR_MESSAGE);
  }

  const processes = (await response.json()) as ProcessDefinition[];

  if (!Array.isArray(processes)) {
    throw new Error(CATALOG_PROCESS_NOT_CONFIGURED_MESSAGE);
  }

  const catalogProcess = processes.find(
    (process) =>
      process.integration?.toLowerCase() === CATALOG_PROCESS_INTEGRATION
  );

  if (!catalogProcess?.id) {
    throw new Error(CATALOG_PROCESS_NOT_CONFIGURED_MESSAGE);
  }

  return catalogProcess.id;
};

const startCatalogSyncJob = async (token: string): Promise<string> => {
  const response = await fetch(CATALOG_SYNC_URL, {
    method: "GET",
    headers: authHeaders(token),
  });

  const body = (await response.json()) as CatalogSyncStartResponse;

  if (!response.ok || body.response === false) {
    throw new Error(
      translateCatalogMessage(body.message ?? "", CATALOG_SYNC_ERROR_MESSAGE)
    );
  }

  return translateCatalogMessage(body.data ?? body.message ?? "", "");
};

const fetchLatestCatalogLog = async (
  token: string,
  processTypeId: number
): Promise<ProcessLogEntry[]> => {
  const params = new URLSearchParams({
    processType: String(processTypeId),
    fromDate: "",
    toDate: "",
    status: "",
    page: "1",
    size: "20",
  });

  const response = await fetch(`${LOG_LOOK_URL}?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(token),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(CATALOG_SYNC_ERROR_MESSAGE);
  }

  const data = (await response.json()) as ProcessLogResponse;
  return parseProcessLogList(data);
};

const useUpdateCatalog = (
  options?: UseUpdateCatalogOptions
): UseUpdateCatalogReturn => {
  const { onSyncComplete } = options ?? {};
  const [syncLoading, setSyncLoading] = useState<boolean>(() =>
    hasActiveCatalogSyncSession(CATALOG_POLL_MAX_WAIT_MS)
  );
  const [syncResult, setSyncResult] = useState<CatalogSyncResult | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [phase, setPhase] = useState<ModalPhase>("confirm");
  const [resultMessage, setResultMessage] = useState<string>("");
  const [resultIsError, setResultIsError] = useState<boolean>(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const onSyncCompleteRef = useRef(onSyncComplete);
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    onSyncCompleteRef.current = onSyncComplete;
  }, [onSyncComplete]);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const applySyncSuccess = useCallback(
    (
      completedLog: ProcessLogEntry,
      processTypeId: number,
      startMessage = ""
    ): void => {
      const result: CatalogSyncResult = {
        ...buildCatalogSyncResult(completedLog),
        processTypeId,
      };

      setSyncResult(result);
      setResultMessage(result.message || startMessage);
      setResultIsError(result.type === "error");
      setPhase("result");
      onSyncCompleteRef.current?.();
    },
    []
  );

  const applySyncFailure = useCallback((msg: string): void => {
    setSyncResult({ type: "error", message: msg });
    setResultMessage(msg);
    setResultIsError(true);
    setPhase("result");
  }, []);

  const finishSyncFlow = useCallback(() => {
    clearPolling();
    clearCatalogSyncSession();
    if (isMountedRef.current) {
      setSyncLoading(false);
    }
  }, [clearPolling]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearPolling();
    };
  }, [clearPolling]);

  const clearSyncResult = useCallback(() => {
    setSyncResult(null);
  }, []);

  const closeModal = useCallback(() => {
    if (phase === "loading") return;
    setIsModalVisible(false);
    setPhase("confirm");
    setResultMessage("");
    setResultIsError(false);
  }, [phase]);

  const openSyncModal = useCallback(() => {
    if (syncLoading) return;
    setPhase("confirm");
    setResultMessage("");
    setResultIsError(false);
    setIsModalVisible(true);
  }, [syncLoading]);

  const waitForCatalogLog = useCallback(
    (
      token: string,
      processTypeId: number,
      startedAt: number,
      baselineLogId: number
    ): Promise<ProcessLogEntry> => {
      const deadline = startedAt + CATALOG_POLL_MAX_WAIT_MS;

      return new Promise((resolve, reject) => {
        const poll = async () => {
          if (!isMountedRef.current) {
            clearPolling();
            reject(new SyncPollAbortedError());
            return;
          }

          if (Date.now() > deadline) {
            clearPolling();
            reject(new Error(CATALOG_SYNC_TIMEOUT_MESSAGE));
            return;
          }

          try {
            const logs = await fetchLatestCatalogLog(token, processTypeId);
            const candidateLog = findCatalogSyncLogAfter(logs, baselineLogId);

            if (!candidateLog) return;

            if (isCatalogSyncRunningStatus(candidateLog.executionStatus)) {
              return;
            }

            if (isCatalogSyncTerminalStatus(candidateLog.executionStatus)) {
              clearPolling();
              resolve(candidateLog);
            }
          } catch {
            // ASSUMPTION: transient network errors during polling are retried.
          }
        };

        void poll();
        pollIntervalRef.current = setInterval(() => {
          void poll();
        }, CATALOG_POLL_INTERVAL_MS);
      });
    },
    [clearPolling]
  );

  const runSyncPolling = useCallback(
    async (
      token: string,
      processTypeId: number,
      startedAt: number,
      baselineLogId: number,
      startMessage = ""
    ): Promise<void> => {
      setSyncLoading(true);
      clearPolling();

      try {
        const logs = await fetchLatestCatalogLog(token, processTypeId);
        const existingLog = findCatalogSyncLogAfter(logs, baselineLogId);

        if (
          existingLog &&
          isCatalogSyncTerminalStatus(existingLog.executionStatus)
        ) {
          if (!isMountedRef.current) return;
          applySyncSuccess(existingLog, processTypeId, startMessage);
          return;
        }

        const completedLog = await waitForCatalogLog(
          token,
          processTypeId,
          startedAt,
          baselineLogId
        );

        if (!isMountedRef.current) return;
        applySyncSuccess(completedLog, processTypeId, startMessage);
      } catch (err) {
        if (err instanceof SyncPollAbortedError) {
          return;
        }

        if (!isMountedRef.current) return;

        const message =
          err instanceof Error
            ? translateCatalogMessage(err.message, CATALOG_SYNC_ERROR_MESSAGE)
            : CATALOG_SYNC_ERROR_MESSAGE;

        applySyncFailure(message);
      } finally {
        if (isMountedRef.current) {
          finishSyncFlow();
        }
      }
    },
    [
      applySyncFailure,
      applySyncSuccess,
      clearPolling,
      finishSyncFlow,
      waitForCatalogLog,
    ]
  );

  const startSync = useCallback(async (): Promise<void> => {
    setPhase("loading");
    setSyncLoading(true);
    setSyncResult(null);
    clearPolling();

    try {
      const token = getBearerToken();
      const processTypeId = await resolveCatalogProcessTypeId(token);
      const priorLogs = await fetchLatestCatalogLog(token, processTypeId);
      const baselineLogId = getLatestCatalogLogId(priorLogs);
      const startedAt = Date.now();

      setCatalogSyncSession({ startedAt, processTypeId, baselineLogId });

      const startMessage = await startCatalogSyncJob(token);
      await runSyncPolling(
        token,
        processTypeId,
        startedAt,
        baselineLogId,
        startMessage
      );
    } catch (err) {
      clearCatalogSyncSession();

      if (!isMountedRef.current) return;

      const message =
        err instanceof Error
          ? translateCatalogMessage(err.message, CATALOG_SYNC_ERROR_MESSAGE)
          : CATALOG_SYNC_ERROR_MESSAGE;

      applySyncFailure(message);
      finishSyncFlow();
    }
  }, [applySyncFailure, clearPolling, finishSyncFlow, runSyncPolling]);

  useEffect(() => {
    if (resumeStartedRef.current) return;

    const session = getCatalogSyncSession();
    if (!session) return;

    if (Date.now() > session.startedAt + CATALOG_POLL_MAX_WAIT_MS) {
      clearCatalogSyncSession();
      setSyncLoading(false);
      return;
    }

    resumeStartedRef.current = true;

    void (async () => {
      try {
        const token = getBearerToken();
        await runSyncPolling(
          token,
          session.processTypeId,
          session.startedAt,
          session.baselineLogId
        );
      } catch {
        clearCatalogSyncSession();
        if (isMountedRef.current) {
          setSyncLoading(false);
        }
      }
    })();
  }, [runSyncPolling]);

  const renderSyncModal = () => {
    if (phase === "confirm") {
      return (
        <Modal
          title={MODAL_TITLE}
          open={isModalVisible}
          onCancel={closeModal}
          footer={[
            <Button key="cancel" onClick={closeModal}>
              Cancelar
            </Button>,
            <Button key="sync" type="primary" onClick={startSync}>
              Iniciar Sincronización
            </Button>,
          ]}
        >
          <p>{MODAL_BODY}</p>
          <ol style={{ paddingLeft: 20, margin: "12px 0" }}>
            {MODAL_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p>
            ¿Deseas iniciar la sincronización exclusiva de tu catálogo de
            productos en este momento?
          </p>
        </Modal>
      );
    }

    if (phase === "loading") {
      return (
        <Modal
          title={MODAL_TITLE}
          open={isModalVisible}
          closable={false}
          maskClosable={false}
          footer={[
            <Button key="cancel" disabled>
              Cancelar
            </Button>,
          ]}
        >
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, fontWeight: 600 }}>
              {SYNC_RUNNING_TITLE}
            </p>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              {SYNC_RUNNING_LINES.map((line) => (
                <span key={line} style={{ display: "block" }}>
                  {line}
                </span>
              ))}
            </p>
          </div>
        </Modal>
      );
    }

    return (
      <Modal
        title={resultIsError ? "Error" : MODAL_TITLE}
        open={isModalVisible}
        onCancel={closeModal}
        footer={[
          <Button key="close" type="primary" onClick={closeModal}>
            Cerrar
          </Button>,
        ]}
      >
        <p>{resultMessage}</p>
      </Modal>
    );
  };

  return {
    syncLoading,
    syncResult,
    clearSyncResult,
    openSyncModal,
    renderSyncModal,
  };
};

export default useUpdateCatalog;
